import { useContext, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Board from "../board/Board";
import ProgressBar from "../progressBar/ProgressBar";
import { useSearchParams } from "react-router-dom";
import { RLocked } from "../svg/GeneralIcons";
// import { RedeemPoints } from "../../modules/redeem";
import { UserContext } from "../context/UseContext";
import { Avatar } from "../Avatar";
import { useAchievements } from "../../api/useAchievements";
import { getValidImageUrl, ordinalSuffix } from "../../utils";
import { Loader } from "../loader/Loader";
import clsx from "clsx";

const tabs = [
  {
    name: "leaderboard",
    label: "LeaderBoard",
    render: () => {
      return <Board />;
    },
  },
  {
    name: "badges",
    label: "Badges",
    render: (profile, name, achievements, loadingAcheivements) => {
      const unlockedBadgeIds =
        achievements?.data?.audience_badges?.map((badge) => badge.id) || [];
      const currentBadge = achievements?.data?.current_badge;
      const rank = achievements?.data?.rank;
      const points = achievements?.data?.point;
      // Sort badges to show unlocked ones first
      const sortedBadges =
        achievements?.data?.badges?.sort((a, b) => {
          const aUnlocked = unlockedBadgeIds.includes(a.id);
          const bUnlocked = unlockedBadgeIds.includes(b.id);

          // Return -1 if a should come before b, 1 if b should come before a, 0 if equal
          if (aUnlocked && !bUnlocked) return -1;
          if (!aUnlocked && bUnlocked) return 1;
          return 0; // Keep original order for badges with same unlock status
        }) || [];

      return (
        <>
          {loadingAcheivements ? (
            <div className="flex justify-center items-center h-[53vh] flex-col gap-3">
              <div className="grid place-self-center h-full">
                <Loader type="spinningBubbles" color="#2741d6" />
              </div>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 2, delay: 0.3 }}
              className="mt-8 grid gap-2.5 justify-items-center"
            >
              {profile?.profile_image ? (
                <div className="p-0.5 border-gradient rounded-full w-fit">
                  <img
                    className="w-[100px] h-[100px] object-cover rounded-full"
                    src={profile?.profile_image}
                    alt="Profile_image"
                    loading="lazy"
                  />
                </div>
              ) : (
                <Avatar name={name} className="w-[100px] h-[100px] !text-3xl" />
              )}
              <h3 className="text-center text-2xl text-primary-main font-semibold">
                {points || 0} pts
              </h3>
              <p className="text-[#00000080]  text-center font-medium">
                {`${currentBadge?.name || "No badge"} | ${
                  rank ? `${ordinalSuffix(rank)} Position` : "Uranked"
                } `}
              </p>
              <div className="w-full">
                <ProgressBar level={1} max={4} isTrailing isleading />
              </div>
              <div className="mt-2 flex flex-col items-start w-full px-4">
                <h4 className="font-bold text-left text-xl text-black">
                  My Badges
                </h4>
                <div className="mt-5 grid grid-cols-12 gap-5 w-full">
                  {sortedBadges.map((x, i) => {
                    const isUnlocked = unlockedBadgeIds.includes(x.id);
                    const badgeName = x?.name;

                    const unlockedImageUrl = getValidImageUrl(
                      x.image_url,
                      "/image/BronzeMedal.png"
                    );
                    const lockedImageUrl = getValidImageUrl(
                      x.image_url,
                      "/image/MedalBlue.png"
                    );

                    return (
                      <div
                        key={i}
                        className={clsx(
                          isUnlocked ? "bg-white" : "bg-[#E3E3E3]",
                          "col-span-4 flex flex-col relative justify-between items-center  p-4 h-32 rounded-[10px]"
                        )}
                      >
                        {isUnlocked ? (
                          <>
                            <img
                              src={unlockedImageUrl}
                              alt={badgeName}
                              className="w-12 h-16"
                              loading="lazy"
                            />

                            <p className="text-black text-xs font-medium text-center">
                              {badgeName}
                            </p>
                          </>
                        ) : (
                          <>
                            <img
                              src={lockedImageUrl}
                              alt={badgeName}
                              className="w-12 h-16 object-contain"
                              loading="lazy"
                            />
                            <p className="text-[#BCB7B7] text-xs font-medium text-center">
                              {badgeName}
                            </p>
                            <span className="absolute flex items-center justify-center -top-2 -right-2 bg-[#E3E3E3] rounded-full h-8 w-8 shadow-sm shadow-[#00000014]">
                              <RLocked />
                            </span>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}
        </>
      );
    },
  },
  {
    name: "redeem",
    label: "Redeem",
    render: (profile) => {
      return (
        <div className="flex flex-col items-center justify-center py-16 min-h-[500px] px-4 relative overflow-hidden">
          {/* Animated background elements */}
          <motion.div
            className="absolute inset-0"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.2 }}
            transition={{
              duration: 2,
              repeat: Infinity,
              repeatType: "reverse",
            }}
          >
            <div className="w-32 h-32 bg-yellow-500 rounded-full absolute top-10 left-10"></div>
            <div className="w-24 h-24 bg-purple-500 rounded-full absolute bottom-20 right-8"></div>
            <div className="w-20 h-20 bg-pink-500 rounded-full absolute top-1/3 right-16"></div>
          </motion.div>

          <div className="text-center relative z-10">
            {/* Celebration emojis with animation */}
            <motion.div className="text-6xl mb-6">🎉🏆🎊</motion.div>

            {/* Main title with gradient and glow effect */}
            <motion.h2
              className="text-3xl font-bold text-[#2741d6] mb-3 drop-shadow-lg"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Winners & Prizes Announcement
            </motion.h2>

            {/* Subtitle with pulsing effect */}
            <motion.p
              className="text-black text-lg font-medium mb-4 px-4"
              animate={{ opacity: [0.7, 1, 0.7] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              Winners and prizes are announced every Sunday!
            </motion.p>

            {/* Profile update reminder */}
            <motion.p
              className="text-gray-600 text-sm font-medium mb-8 px-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              📝 Make sure your profile is updated so your name appears
              correctly on the leaderboard.
            </motion.p>

            {/* Decorative elements */}
            <div className="flex justify-center space-x-4 text-4xl mb-6">
              <motion.span
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
              >
                🎁
              </motion.span>
              <motion.span
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
              >
                💰
              </motion.span>
            </div>

            {/* Floating confetti-like elements */}
            <motion.div className="absolute -top-4 left-1/4 text-2xl">
              <motion.span
                animate={{
                  scale: [1, 1.2, 1],
                  rotate: [0, 10, -10, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                ⭐
              </motion.span>
            </motion.div>
            <motion.div className="absolute top-8 right-1/4 text-2xl">
              <motion.span
                animate={{
                  scale: [1, 1.8, 1],
                  rotate: [0, 10, -10, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                🎈
              </motion.span>
            </motion.div>
          </div>
        </div>
      );
    },
  },
];

const tabContentVariants = {
  initial: {
    y: 10,
    opacity: 0,
  },
  enter: {
    y: 0,
    opacity: 1,
  },
  exit: {
    y: -10,
    opacity: 0,
  },
};

function AchievementUI() {
  const { profile } = useContext(UserContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const { getArenaAcheivements, achievements, loadingAcheivements } =
    useAchievements();

  useEffect(() => {
    getArenaAcheivements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper function to get tab from search params
  const getTabFromParams = () => {
    const tabParam = searchParams.get("tab");
    const foundTab = tabs.find((tab) => tab.name === tabParam);
    return foundTab || tabs[0]; // Default to first tab if not found
  };

  const [activeTab, setActiveTab] = useState(getTabFromParams);

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    const foundTab = tabs.find((tab) => tab.name === tabParam);
    const tabFromUrl = foundTab || tabs[0];
    if (tabFromUrl.name !== activeTab.name) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams, activeTab.name]);

  useEffect(() => {
    if (!searchParams.get("tab")) {
      setSearchParams({ tab: tabs[0].name });
    }
  }, [searchParams, setSearchParams]);

  const handleClick = (e, tab) => {
    e.preventDefault();
    setActiveTab(tab);
    setSearchParams({ tab: tab.name });
  };

  const isSelected = (tab) => activeTab.name === tab.name;
  const name = `${profile.first_name} ${profile.last_name}`;
  return (
    <div className="ach-tabWrapper h-full">
      <div className="ach-tabHeader mx-4">
        {tabs.map((tab) => (
          <div
            key={tab.name}
            className={[
              "ach-tabItem cursor-pointer",
              isSelected(tab) ? "ach-selected" : "",
            ].join(" ")}
          >
            <span onClick={(e) => handleClick(e, tab)}>{tab.label}</span>

            {isSelected(tab) && (
              <motion.div layoutId="indicator" className={"ach-indicator"} />
            )}
          </div>
        ))}
      </div>

      <div className={"ach-tabContent, pb-8"}>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab.name || "empty"}
            variants={tabContentVariants}
            initial="initial"
            animate="enter"
            exit="exit"
            transition={{
              duration: 0.3,
            }}
          >
            {activeTab &&
              activeTab?.render(
                profile,
                name,
                achievements,
                loadingAcheivements
              )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

export default AchievementUI;
